import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearAuth, getUsername } from "@/lib/auth";
import ChangePasswordModal from "@/components/feature/ChangePasswordModal";
import { SITE_IMAGES } from "@/lib/siteAssets";

const ADMIN_PAGE_TITLES: Record<string, string> = {
  "/admin/main-slider": "Main Slider",
  "/admin/pipeline": "Main Slider",
  "/admin/products": "Product",
  "/admin/bio-art": "Bio-Art",
  "/admin/newsroom": "Newsroom",
  "/admin/publications": "Publications",
};

const navItems = [
  {
    href: "/admin/main-slider",
    label: "Main Slider",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M4 6h16M4 12h16M4 18h7" />
      </svg>
    ),
  },
  {
    href: "/admin/products",
    label: "Product",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 10V7" />
      </svg>
    ),
  },
  {
    href: "/admin/bio-art",
    label: "Bio-Art",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    href: "/admin/publications",
    label: "Publications",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    href: "/admin/newsroom",
    label: "Newsroom",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    ),
  },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const username = getUsername();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const title = ADMIN_PAGE_TITLES[location.pathname] ?? "Admin";

  const handleLogout = () => {
    clearAuth();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="h-screen bg-[#f6f9fc] flex overflow-hidden">

      {/* 모바일 오버레이 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 사이드바 */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-64 z-30 flex flex-col shrink-0
          bg-gradient-to-b from-[#2c4964] to-[#243a52]
          shadow-xl shadow-[#2c4964]/20
          transform transition-transform duration-300 ease-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:relative md:flex
        `}
      >
        {/* 브랜드 */}
        <div className="px-4 pt-6 pb-5">
          <div className="flex items-center gap-3 rounded-xl bg-white/[0.06] border border-white/[0.08] px-3.5 py-3.5">
            <img
              src={SITE_IMAGES.logo}
              alt="Biocon"
              className="h-8 w-auto brightness-0 invert opacity-95 shrink-0"
            />
            <p className="text-white text-sm font-semibold tracking-wide leading-snug">
              Biocon Administrator
            </p>
          </div>
        </div>

        {/* 메뉴 */}
        <nav className="px-3 py-2 space-y-0.5 shrink-0">
          {navItems.map((item) => {
            const active =
              location.pathname === item.href ||
              (item.href === "/admin/main-slider" && location.pathname === "/admin/pipeline");
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group relative flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium
                  transition-colors duration-150
                  ${active
                    ? "bg-white/12 text-white shadow-sm"
                    : "text-white/55 hover:bg-white/[0.07] hover:text-white/90"}
                `}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-[#4da3e8]" />
                )}
                <span
                  className={`
                    flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors
                    ${active
                      ? "bg-[#1977cc]/30 text-white"
                      : "bg-white/[0.05] text-white/70 group-hover:bg-white/10 group-hover:text-white"}
                  `}
                >
                  {item.icon}
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* 하단 */}
        <div className="px-3 py-4 mt-auto space-y-0.5">
          <div className="h-px bg-white/[0.08] mb-3 mx-1" />
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/45 hover:bg-white/[0.06] hover:text-white/80 transition-colors duration-150"
          >
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.04]">
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </span>
            사이트 홈
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/45 hover:bg-white/[0.06] hover:text-white/80 transition-colors duration-150 cursor-pointer"
          >
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.04]">
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </span>
            로그아웃
          </button>
        </div>
      </aside>

      {/* 메인 영역 */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        {/* 상단 헤더 */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/80 px-5 py-4 flex items-center gap-4 sticky top-0 z-10">
          {/* 모바일 햄버거 */}
          <button
            className="md:hidden text-[#2c4964] hover:text-[#1977cc] transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <h1 className="text-base font-bold text-[#2c4964] flex-1">{title}</h1>

          {/* 우측 사용자 정보 */}
          <div className="flex items-center gap-2 md:gap-3">
            <span className="hidden sm:inline text-sm text-gray-400">{username ?? "Admin"}</span>
            <button
              type="button"
              onClick={() => setPasswordModalOpen(true)}
              className="text-xs text-gray-400 hover:text-[#1977cc] transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="hidden sm:inline">비밀번호 변경</span>
              <span className="sm:hidden">비밀번호</span>
            </button>
          </div>
        </header>

        <ChangePasswordModal
          open={passwordModalOpen}
          onClose={() => setPasswordModalOpen(false)}
        />

        {/* 컨텐츠 */}
        <main className="flex-1 p-5 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
